import React from 'react';
import CalControlView from './CalControlView';
import CalWeekMonthHeader from './CalWeekMonthHeader';
import CalWeekLaneRow from './CalWeekLaneRow';

const DocCalGridBoard = ({
    weeks,
    monthSpans,
    groupData,
    groupKeys,
    sarasDnDManager,
    laneWidth,
    currentMonthKey,
    controlView,
    calendarControl,
    isTabletWidthOrLess,
    monthSummaryMap,
    onMoveDate,
    weekSummaryMap,
    onVisibleWeeksChange,
    onVisibleMonthsChange,
    onExtendLeft,
    onExtendRight
}) => {
    const scrollRef = React.useRef();
    const [viewportWidth, setViewportWidth] = React.useState(0);
    const [scrollLeft, setScrollLeft] = React.useState(0);
    const previousFirstKey = React.useRef(null);
    const previousKeyCount = React.useRef(0);
    const centeredOnce = React.useRef(false);

    const totalWidth = Math.max(groupKeys.length * laneWidth, laneWidth);
    const visibleStartIndex = Math.max(0, Math.floor(scrollLeft / laneWidth) - 2);
    const visibleEndIndex = Math.min(groupKeys.length - 1, Math.ceil((scrollLeft + viewportWidth) / laneWidth) + 2);
    const visibleWeekMeta = weeks.slice(visibleStartIndex, visibleEndIndex + 1).map((week, offset) => ({
        ...week,
        index: visibleStartIndex + offset
    }));

    const visibleMonthSpans = monthSpans.filter((span) => {
        const spanEnd = span.startIndex + span.weekCount - 1;
        return span.startIndex <= visibleEndIndex && spanEnd >= visibleStartIndex;
    });

    const moveFocusToCurrentMonth = () => {
        if (!scrollRef.current) return;

        const currentSpan = monthSpans.find((span) => span.monthKey === currentMonthKey);
        if (!currentSpan) return;

        const containerWidth = scrollRef.current.clientWidth;
        const targetCenter = ((currentSpan.startIndex + (currentSpan.weekCount / 2)) * laneWidth);
        scrollRef.current.scrollTo({
            left: Math.max(0, targetCenter - (containerWidth / 2)),
            behavior: 'smooth'
        });
    };

    const moveToCurrentMonthStart = () => {
        if (!scrollRef.current) return;

        const currentSpan = monthSpans.find((span) => span.monthKey === currentMonthKey);
        if (!currentSpan) return;

        scrollRef.current.scrollLeft = Math.max(0, currentSpan.startIndex * laneWidth);
    };

    React.useEffect(() => {
        if (!scrollRef.current) return;

        if (!centeredOnce.current) {
            moveToCurrentMonthStart();
            centeredOnce.current = true;
        }

        const currentFirstKey = groupKeys[0];
        if (previousFirstKey.current && currentFirstKey !== previousFirstKey.current) {
            const oldFirstIndexInNew = groupKeys.indexOf(previousFirstKey.current);
            if (oldFirstIndexInNew > 0) {
                scrollRef.current.scrollLeft += oldFirstIndexInNew * laneWidth;
            }
        }

        previousFirstKey.current = currentFirstKey;
        previousKeyCount.current = groupKeys.length;
        setViewportWidth(scrollRef.current.clientWidth);
        setScrollLeft(scrollRef.current.scrollLeft);
    }, [currentMonthKey, groupKeys, laneWidth, monthSpans]);

    React.useEffect(() => {
        if (!onVisibleWeeksChange) return;

        onVisibleWeeksChange(visibleWeekMeta.map((week) => week.groupKey));
    }, [onVisibleWeeksChange, visibleWeekMeta]);

    React.useEffect(() => {
        if (!onVisibleMonthsChange) return;

        onVisibleMonthsChange(visibleMonthSpans.map((month) => month.monthKey));
    }, [onVisibleMonthsChange, visibleMonthSpans]);

    const onScroll = (event) => {
        const nextScrollLeft = event.target.scrollLeft;
        const nextViewportWidth = event.target.clientWidth;

        setScrollLeft(nextScrollLeft);
        setViewportWidth(nextViewportWidth);

        if (nextScrollLeft < laneWidth * 2 && onExtendLeft) {
            onExtendLeft();
        }

        if ((nextScrollLeft + nextViewportWidth) > (totalWidth - (laneWidth * 3)) && onExtendRight) {
            onExtendRight();
        }
    };

    return (
        <div className="uiSecContents uiCalGridContainer" style={{ overflow: 'hidden', padding: '0', position: 'relative' }}>
            <CalControlView
                viewData={controlView}
                calendarControl={calendarControl}
                onFocusClick={moveFocusToCurrentMonth}
            />
            <div
                ref={scrollRef}
                className="uiCalGridBoardScroller"
                style={{ overflowX: 'auto', overflowY: 'hidden' }}
                onScroll={onScroll}
            >
                <div className="uiCalGridBoardCanvas" style={{ position: 'relative', width: `${totalWidth}px` }}>
                    <CalWeekMonthHeader
                        monthSpans={visibleMonthSpans}
                        laneWidth={laneWidth}
                        visibleStartIndex={visibleStartIndex}
                        monthSummaryMap={monthSummaryMap}
                    />
                    <CalWeekLaneRow
                        groupData={groupData}
                        visibleWeekMeta={visibleWeekMeta}
                        sarasDnDManager={sarasDnDManager}
                        calendarControl={calendarControl}
                        isTabletWidthOrLess={isTabletWidthOrLess}
                        onMoveDate={onMoveDate}
                        weekSummaryMap={weekSummaryMap}
                        laneWidth={laneWidth}
                    />
                </div>
            </div>
        </div>
    );
};

export default DocCalGridBoard;
